import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { ButtonV2, DownloadIcon, IconButton, Tabs, toast } from '@utilities';
import {
  createVideoBookmark,
  deleteVideoBookmark,
  listVideoBookmarksResponse,
} from '@api/instant_survey';

import Bookmark from './components/Bookmark';
import Summary from './components/Summary';
import Transcript from './components/Transcript';

import styles from './_index.module.scss';

const VideoPlayer = ({
  hasNext = false,
  hasPrevious = false,
  onBookmarkChange,
  onNext,
  onPrevious,
  questionId,
  responseId,
  videoData,
}) => {
  const videoRef = useRef(null);
  const [activeTab, setActiveTab] = useState('summary');
  const [bookmarks, setBookmarks] = useState([]);
  const [isLoadingBookmarks, setIsLoadingBookmarks] = useState(false);
  const endTimestampRef = useRef(null);

  const tabOptions = [
    { label: 'Summary', value: 'summary' },
    { label: 'Transcript', value: 'transcript' },
    { label: 'Bookmarks', value: 'bookmarks' },
  ];

  useEffect(() => {
    if (!responseId || !questionId) return;

    const fetchBookmarks = async () => {
      setIsLoadingBookmarks(true);
      try {
        const data = await listVideoBookmarksResponse({ questionId, responseId });
        setBookmarks(data?.results || data || []);
      } catch (error) {
        toast.error('Failed to load bookmarks');
      } finally {
        setIsLoadingBookmarks(false);
      }
    };

    fetchBookmarks();
  }, [responseId, questionId]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => {
      if (endTimestampRef.current !== null && video.currentTime >= endTimestampRef.current) {
        video.pause();
        endTimestampRef.current = null;
      }
    };

    video.addEventListener('timeupdate', handleTimeUpdate);

    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
    };
  }, []);

  const handleSeekToTimestamp = (startTimestamp, endTimestamp = null) => {
    if (videoRef.current) {
      videoRef.current.currentTime = startTimestamp;
      endTimestampRef.current = endTimestamp;
      // Optional: play the video if it's paused
      if (videoRef.current.paused) {
        videoRef.current.play();
      }
    }
  };

  const handleAddBookmark = async (text, start_ts, end_ts, itemIndices = []) => {
    if (!responseId || !questionId) {
      toast.error('Unable to save bookmark: missing response ID or question ID');
      return;
    }

    try {
      const newBookmark = await createVideoBookmark({
        end_ts,
        item_indices: itemIndices,
        questionId,
        responseId,
        start_ts,
        text: text.trim(),
      });
      setBookmarks((prev) => [...prev, newBookmark]);
      toast.success('Bookmark added successfully');
      if (onBookmarkChange) {
        onBookmarkChange();
      }
    } catch (error) {
      toast.error('Failed to save bookmark');
    }
  };

  const handleDeleteBookmark = async (bookmarkId) => {
    try {
      await deleteVideoBookmark({ bookmarkId });
      setBookmarks((prev) => prev.filter((bookmark) => bookmark.id !== bookmarkId));
      toast.success('Bookmark deleted successfully');
      if (onBookmarkChange) {
        onBookmarkChange();
      }
    } catch (error) {
      toast.error('Failed to delete bookmark');
    }
  };

  const handleDownload = () => {
    if (!videoData?.video_url) {
      return;
    }

    // Create a temporary anchor element to trigger download
    const link = document.createElement('a');
    link.href = videoData.video_url;
    link.download = `video_response_${videoData.id || Date.now()}.mp4`;
    link.style.display = 'none';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (!videoData) {
    return (
      <div className={styles['video-player']}>
        <p>No video data available. Please navigate from the video list.</p>
      </div>
    );
  }

  return (
    <div className={styles['video-player']}>
      <div className={styles['video-player-layout']}>
        <div className={styles['video-player-section']}>
          <div className={styles['video-player-header']}>
            <h2>Video Response</h2>
            <div className={styles['video-player-actions']}>
              <IconButton
                data-testid="video-player-download-button"
                onClick={handleDownload}
                tooltip="Download"
              >
                <DownloadIcon />
              </IconButton>
              <IconButton.Favorite tooltip="Add to favorites" />
            </div>
          </div>
          <div className={styles['video-player-container']}>
            <div className={styles['video-player-element']}>
              {videoData.video_url ? (
                // Respondent-submitted videos have no caption track; the transcript tab
                // provides the accessible text alternative.
                // eslint-disable-next-line jsx-a11y/media-has-caption
                <video
                  controls
                  data-testid="video"
                  poster={videoData.thumbnail_url}
                  ref={videoRef}
                  src={videoData.video_url}
                >
                  Your browser does not support the video tag.
                </video>
              ) : (
                <div className={styles['video-player-no-content']}>No video available</div>
              )}
            </div>
          </div>
        </div>

        <div className={styles['video-player-transcript-wrapper']}>
          <div className={styles['video-player-tabs-wrapper']}>
            <Tabs
              aria-label="Video details"
              onChange={setActiveTab}
              options={tabOptions}
              value={activeTab}
            />
          </div>
          <div className={styles['video-player-tab-content']}>
            {activeTab === 'summary' && (
              <Summary aiSummary={videoData.ai_summary} data-testid="video-player-summary-tab" />
            )}
            {activeTab === 'transcript' && (
              <Transcript
                audioSegments={videoData.audio_segments}
                bookmarks={bookmarks}
                data-testid="video-player-transcript-tab"
                items={videoData.items}
                onAddBookmark={handleAddBookmark}
                onSeekToTimestamp={handleSeekToTimestamp}
              />
            )}
            {activeTab === 'bookmarks' && (
              <Bookmark
                bookmarks={bookmarks}
                data-testid="video-player-bookmarks-tab"
                isLoadingBookmarks={isLoadingBookmarks}
                onDeleteBookmark={handleDeleteBookmark}
                onSeekToTimestamp={handleSeekToTimestamp}
              />
            )}
          </div>
        </div>
      </div>

      <div className={styles['video-player-navigation']}>
        <ButtonV2 disabled={!hasPrevious} onClick={onPrevious}>
          Previous Video
        </ButtonV2>
        <ButtonV2 disabled={!hasNext} onClick={onNext}>
          Next Video
        </ButtonV2>
      </div>
    </div>
  );
};

VideoPlayer.propTypes = {
  hasNext: PropTypes.bool,
  hasPrevious: PropTypes.bool,
  onBookmarkChange: PropTypes.func,
  onNext: PropTypes.func,
  onPrevious: PropTypes.func,
  questionId: PropTypes.number,
  responseId: PropTypes.number,
  videoData: PropTypes.shape({
    ai_summary: PropTypes.string,
    audio_segments: PropTypes.array,
    id: PropTypes.number,
    items: PropTypes.array,
    thumbnail_url: PropTypes.string,
    video_url: PropTypes.string,
  }),
};

export default VideoPlayer;
